import type { MapLocation } from "@/Shared/Maps/MapLocation";
import type { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { calculatePointsOnArc } from "@/Helpers/calculatePointsOnArc";
import { hasValidLatitudeLongitude } from "./hasValidLatitudeLongitude";
import { latitudeLongitudeToNumbers } from "./latitudeLongitudeToNumbers";
import { pathColorForShipmentState } from "./pathColorForShipmentState";

function addArc(
  map: mapboxgl.Map,
  pickup: Partial<MapLocation> | undefined,
  delivery: Partial<MapLocation> | undefined,
  shipmentState: ShipmentState,
) {
  // Only draw the route line when BOTH endpoints have usable coordinates.
  // A missing or (0,0) coordinate would otherwise anchor the line in the ocean
  // off Africa (ENG-4342) — skip the arc and let the valid point stand alone.
  if (
    !hasValidLatitudeLongitude(pickup) ||
    !hasValidLatitudeLongitude(delivery)
  ) {
    return;
  }

  const pathColor = pathColorForShipmentState(shipmentState);
  const points = calculatePointsOnArc(
    pickup.latitudeLongitude,
    delivery.latitudeLongitude,
    10,
  ).map((c) => latitudeLongitudeToNumbers(c));

  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i];
    const end = points[i + 1];
    if (start === undefined || end === undefined) {
      continue;
    }
    map.addSource("routeSource" + i, {
      type: "geojson",
      data: {
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates: [start, end],
        },
      },
    });

    map.addLayer({
      id: "route" + i,
      source: "routeSource" + i,
      type: "line",
      paint: {
        "line-width": 2,
        "line-color": pathColor,
      },
    });
  }
}

function removeArc(map: mapboxgl.Map) {
  for (let i = 0; i < 10; i++) {
    if (map.getLayer("route" + i)) {
      map.removeLayer("route" + i);
    }

    if (map.getSource("routeSource" + i)) {
      map.removeSource("routeSource" + i);
    }
  }
}

export function setupArc(
  map: mapboxgl.Map,
  pickup: Partial<MapLocation> | undefined,
  delivery: Partial<MapLocation> | undefined,
  shipmentState: ShipmentState,
) {
  if (pickup === undefined || delivery === undefined) {
    removeArc(map);
  } else {
    // We might be moving the arc
    removeArc(map);
    addArc(map, pickup, delivery, shipmentState);
  }
}
